// nuolino-admin/src/components/ui/Input.tsx
// Input base. Label + helper + error. Acessível (aria-describedby, aria-invalid).
// Aceita props de RHF (UseFormRegisterReturn) via spread ou via prop `registration`.

import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';
import { cn } from '@/lib/cn';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'ref'> {
  label?: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
  leftAddon?: ReactNode;
  rightAddon?: ReactNode;
  /** Spread do RHF (register('campo')) — quando presente, é aplicado ao <input> interno. */
  registration?: UseFormRegisterReturn;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, helper, error, leftAddon, rightAddon, className, id, registration, ...props },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? reactId;
  const helperId = helper ? `${inputId}-helper` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [helperId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium text-text">
          {label}
        </label>
      )}
      <div
        className={cn(
          'flex h-11 items-center rounded-md border bg-surface px-3',
          'transition-colors duration-fast',
          error ? 'border-danger-500' : 'border-border',
          'focus-within:border-action focus-within:ring-2 focus-within:ring-[var(--color-focus-ring)]',
        )}
      >
        {leftAddon && (
          <span aria-hidden="true" className="mr-2 text-text-muted">
            {leftAddon}
          </span>
        )}
        <input
          ref={(node) => {
            registration?.ref(node);
            if (typeof ref === 'function') ref(node);
            else if (ref) ref.current = node;
          }}
          id={inputId}
          name={registration?.name ?? props.name}
          onChange={(e) => {
            registration?.onChange(e);
            props.onChange?.(e);
          }}
          onBlur={(e) => {
            registration?.onBlur(e);
            props.onBlur?.(e);
          }}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(
            'h-full w-full bg-transparent text-base text-text outline-none focus-visible:shadow-none',
            'placeholder:text-text-muted',
            'disabled:cursor-not-allowed disabled:opacity-60',
            className,
          )}
          {...props}
        />
        {rightAddon && (
          <span aria-hidden="true" className="ml-2 text-text-muted">
            {rightAddon}
          </span>
        )}
      </div>
      {helper && !error && (
        <p id={helperId} className="text-sm text-text-muted">
          {helper}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-sm text-danger-500">
          {error}
        </p>
      )}
    </div>
  );
});
